import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Button, Spin, message, Switch, Alert, Space, Popover, Divider, Tabs } from 'antd';
import { QuestionCircleOutlined, ShopOutlined, LockOutlined, KeyOutlined, CheckCircleOutlined, ApiOutlined } from '@ant-design/icons';
import { supabase } from '../../utils/supabase';
import { integrationsApi } from '../../utils/api';
const { TabPane } = Tabs;

const ShopifySettings = () => {
  // Formulaire pour les paramètres Webhook
  const [webhookForm] = Form.useForm(); // Formulaire indépendant pour les webhooks
  // Formulaire pour les paramètres API Shopify
  const [apiForm] = Form.useForm(); // Formulaire indépendant pour l'API Shopify
  const [loading, setLoading] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [settings, setSettings] = useState(null);

  /**
   * Lit l'identifiant de la boutique sélectionnée afin que les identifiants
   * Shopify ne soient jamais partagés entre deux boutiques du même compte.
   * @returns {string|null} Identifiant de la boutique active ou null.
   */
  const getActiveShopId = () => window.localStorage.getItem('activeShopId');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const shopId = getActiveShopId();
      if (!shopId) throw new Error('Sélectionnez une boutique avant de configurer Shopify.');

      const { data, error } = await supabase
        .from('settings')
        .select('*')
        .eq('user_id', user.id)
        .eq('shop_id', shopId)
        .eq('type', 'shopify')
        .single();

      if (error && error.code !== 'PGSQL_ERROR_NO_DATA_FOUND') {
        throw error;
      }

      if (data) {
        setSettings(data);
        // Remplir les champs du formulaire Webhook
        webhookForm.setFieldsValue({
          shop_domain: data.shop_domain,
          webhook_secret: data.webhook_secret,
          is_active: data.is_active
        });
        // Remplir les champs du formulaire API Shopify
        apiForm.setFieldsValue({
          shop_name: data.shop_name,
          api_key: data.api_key ? '••••••••••••••••' : '',
          api_password: data.api_password ? '••••••••••••••••' : '',
          api_version: data.api_version || '2023-04',
          is_active: data.is_active
        });
      }
    } catch (error) {
      console.error('Erreur lors du chargement des paramètres Shopify:', error);
      message.error('Impossible de charger les paramètres Shopify');
    } finally {
      setLoading(false);
    }
  };

  // Sauvegarde des paramètres Webhook
  const saveWebhookSettings = async (values) => {
    try {
      setSavingSettings(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const shopId = getActiveShopId();
      if (!shopId) throw new Error('Sélectionnez une boutique avant de configurer Shopify.');
      // On ne touche qu'aux champs Webhook
      const { error } = await supabase
        .from('settings')
        .upsert({
          user_id: user.id,
          shop_id: shopId,
          type: 'shopify',
          shop_domain: values.shop_domain,
          webhook_secret: values.webhook_secret,
          is_active: values.is_active,
          updated_at: new Date().toISOString(),
          // On conserve les autres champs existants
          shop_name: settings?.shop_name || '',
          api_key: settings?.api_key || '',
          api_password: settings?.api_password || '',
          api_version: settings?.api_version || '2023-04',
        }, {
          onConflict: 'user_id,shop_id,type'
        });
      if (error) throw error;
      message.success('Paramètres Webhook Shopify sauvegardés avec succès');
      loadSettings();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde des paramètres Webhook Shopify:', error);
      message.error('Impossible de sauvegarder les paramètres Webhook Shopify');
    } finally {
      setSavingSettings(false);
    }
  };

  // Sauvegarde des paramètres API Shopify
  const saveApiSettings = async (values) => {
    try {
      setSavingSettings(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const shopId = getActiveShopId();
      if (!shopId) throw new Error('Sélectionnez une boutique avant de configurer Shopify.');
      // Ne pas écraser les identifiants existants si des astérisques sont fournis
      const apiKey = values.api_key?.includes('•') ? (settings?.api_key || '') : values.api_key;
      const apiPassword = values.api_password?.includes('•') ? (settings?.api_password || '') : values.api_password;
      const { error } = await supabase
        .from('settings')
        .upsert({
          user_id: user.id,
          shop_id: shopId,
          type: 'shopify',
          shop_name: values.shop_name,
          api_key: apiKey,
          api_password: apiPassword,
          api_version: values.api_version,
          is_active: values.is_active,
          updated_at: new Date().toISOString(),
          // On conserve les autres champs existants
          shop_domain: settings?.shop_domain || '',
          webhook_secret: settings?.webhook_secret || '',
        }, {
          onConflict: 'user_id,shop_id,type'
        });
      if (error) throw error;
      message.success('Paramètres API Shopify sauvegardés avec succès');
      loadSettings();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde des paramètres API Shopify:', error);
      message.error('Impossible de sauvegarder les paramètres API Shopify');
    } finally {
      setSavingSettings(false);
    }
  };

  // Test de connexion API Shopify (utilise le formulaire API uniquement)
  const testConnection = async () => {
    try {
      setTestingConnection(true);
      setConnectionStatus(null);
      // Récupérer les valeurs actuelles du formulaire API Shopify
      const values = apiForm.getFieldsValue();
      // Utiliser les valeurs actuelles ou celles stockées en fonction du masquage
      const apiKey = values.api_key?.includes('•') ? (settings?.api_key || '') : values.api_key;
      const apiPassword = values.api_password?.includes('•') ? (settings?.api_password || '') : values.api_password;
      // Tester la connexion via votre backend
      const response = await integrationsApi.testShopifyConnection(values.shop_name, apiKey, apiPassword, values.api_version);
      const result = await response.data;
      if (result && result.success) {
        setConnectionStatus({
          success: true,
          message: 'Connexion à Shopify réussie!'
        });
        message.success('Connexion à Shopify réussie!');
      } else {
        setConnectionStatus({
          success: false,
          message: `Échec de la connexion: ${result.message || 'Vérifiez vos identifiants'}`
        });
        message.error(`Échec de la connexion: ${result.message || 'Vérifiez vos identifiants'}`);
      }
    } catch (error) {
      console.error('Erreur lors du test de connexion:', error);
      setConnectionStatus({
        success: false,
        message: 'Erreur lors du test de connexion'
      });
      message.error('Erreur lors du test de connexion');
    } finally {
      setTestingConnection(false);
    }
  };

  const HelpIcon = ({ title, content }) => (
    <Popover title={title} content={content} placement="right">
      <QuestionCircleOutlined className="ml-1 text-gray-400" />
    </Popover>
  );

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Spin tip="Chargement des paramètres Shopify..." />
      </div>
    );
  }

  return (
    <Card 
      title={
        <div className="flex items-center">
          <ShopOutlined className="mr-2" />
          <span>Intégration Shopify</span>
        </div>
      }
      className="mb-6"
    >
      <Tabs defaultActiveKey="webhooks">
        <TabPane 
          tab={
            <span>
              <ApiOutlined />
              Webhooks entrants
            </span>
          } 
          key="webhooks"
        >
          <Form
            form={webhookForm} // Formulaire indépendant pour les webhooks
            layout="vertical"
            onFinish={saveWebhookSettings} // Sauvegarde dédiée aux webhooks
            initialValues={{
              shop_domain: '',
              webhook_secret: '',
              is_active: true
            }}
          >
            <Form.Item
              name="shop_domain"
              label={
                <span>
                  Domaine de la boutique Shopify
                  <HelpIcon 
                    title="Domaine de la boutique" 
                    content="Le domaine complet de votre boutique Shopify (ex: ma-boutique.myshopify.com)" 
                  />
                </span>
              }
              rules={[
                { required: true, message: 'Veuillez entrer le domaine de votre boutique Shopify' }
              ]}
            >
              <Input 
                placeholder="ma-boutique.myshopify.com" 
                prefix={<ShopOutlined className="site-form-item-icon" />}
              />
            </Form.Item>

            <Form.Item
              name="webhook_secret"
              label={
                <span>
                  Secret du webhook
                  <HelpIcon 
                    title="Secret du webhook" 
                    content="Le secret utilisé pour vérifier l'authenticité des webhooks Shopify. Vous pouvez le trouver dans l'admin Shopify sous Paramètres &gt; Notifications &gt; Webhooks, indiqué comme 'Vos webhooks seront signés avec:'" 
                  />
                </span>
              }
              rules={[
                { required: true, message: 'Veuillez entrer le secret du webhook Shopify' }
              ]}
            >
              <Input.Password 
                placeholder="Secret du webhook Shopify" 
                prefix={<LockOutlined className="site-form-item-icon" />}
              />
            </Form.Item>

            <Alert
              message="Configuration des webhooks"
              description={
                <div>
                  <p>Pour recevoir les notifications de Shopify, configurez des webhooks dans l'admin de votre boutique Shopify avec les détails suivants:</p>
                  <ul>
                    <li><strong>URL:</strong> {window.location.origin}/api/shopify/webhooks</li>
                    <li><strong>Format:</strong> JSON</li>
                    <li><strong>Événements à écouter:</strong> 
                      <ul>
                        <li>orders/create</li>
                        <li>orders/updated</li>
                        <li>orders/cancelled</li>
                      </ul>
                    </li>
                    <li><strong>Secret du webhook:</strong> Vous le trouverez dans Paramètres &gt; Notifications &gt; Webhooks, indiqué comme "Vos webhooks seront signés avec:"</li>
                  </ul>
                </div>
              }
              type="info"
              showIcon
              className="mb-4"
            />

            <Divider />

            <div className="flex justify-end">
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={savingSettings}
              >
                Enregistrer les paramètres
              </Button>
            </div>
          </Form>
        </TabPane>

        <TabPane 
          tab={
            <span>
              <ApiOutlined />
              API Shopify
            </span>
          } 
          key="api"
        >
          <Form
            form={apiForm} // Formulaire indépendant pour l'API Shopify
            layout="vertical"
            onFinish={saveApiSettings} // Sauvegarde dédiée à l'API Shopify
            initialValues={{
              shop_name: '',
              api_key: '',
              api_password: '',
              api_version: '2023-04',
              is_active: true
            }}
          >
            <Form.Item
              name="shop_name"
              label={
                <span>
                  Nom de la boutique Shopify
                  <HelpIcon 
                    title="Nom de la boutique" 
                    content="Entrez le nom de votre boutique Shopify sans le '.myshopify.com'" 
                  />
                </span>
              }
              rules={[
                { required: true, message: 'Veuillez entrer le nom de votre boutique Shopify' }
              ]}
            >
              <Input 
                placeholder="votre-boutique" 
                addonAfter=".myshopify.com"
                prefix={<ShopOutlined className="site-form-item-icon" />}
              />
            </Form.Item>


            <Form.Item
              name="api_password"
              label={
                <span>
                  Jeton d'accès de l'API Admin

                  <HelpIcon 
                    title="Jeton d'accès de l'API Admin" 
                    content="Le jeton d'accès associé à votre application privée Shopify" 
                  />
                </span>
              }
              rules={[
                { required: true, message: 'Veuillez entrer votre jeton d’accès de l’API Admin' }
              ]}
            >
              <Input.Password 
                placeholder="Jeton d'accès de l'API Admin" 
                prefix={<LockOutlined className="site-form-item-icon" />}
              />
            </Form.Item>


            <Form.Item
              name="is_active"
              valuePropName="checked"
              label="Activer l'intégration Shopify"
            >
              <Switch />
            </Form.Item>

            {connectionStatus && (
              <Alert
                message={connectionStatus.success ? "Connexion réussie" : "Échec de la connexion"}
                description={connectionStatus.message}
                type={connectionStatus.success ? "success" : "error"}
                showIcon
                className="mb-4"
              />
            )}

            <Divider />

            <div className="flex justify-between">
              <Button 
                onClick={testConnection} 
                loading={testingConnection}
                type="default"
              >
                Tester la connexion
              </Button>
              
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={savingSettings}
              >
                Enregistrer les paramètres
              </Button>
            </div>
          </Form>
        </TabPane>
      </Tabs>
    </Card>
  );
};

export default ShopifySettings;
